/**
 * Fetch-all-pages export helper for server-backed grids.
 *
 * A grid's CSV export needs every row across the full filtered/sorted result
 * set, not just the on-screen page, so it must page a list endpoint to
 * exhaustion and unwrap whichever envelope shape that endpoint answers with.
 * `unwrapGridEnvelope` is the single source of that shape knowledge —
 * `useServerDataGrid.js`'s live fetch path imports it too, so a grid's export
 * and its on-screen data can't silently drift apart.
 *
 * Contains:
 * - unwrapGridEnvelope — response -> { data, count }, shared with useServerDataGrid's live fetch path
 * - fetchAllPages — pages a fetcher to exhaustion, returns every row
 */

import _ from 'lodash';

const DEFAULT_PAGE_SIZE = 500;

/**
 * Normalize a grid list response into rows + total count. Recognizes: `{
 * results, count }`, `{ payload: { results, count|meta } }`, `{ data,
 * count|meta }` (the odl_app `GridReadMixin.list()` envelope), `{ payload: {
 * data, ... } }`, and a bare array.
 *
 * @param {object|Array} response
 * @returns {{ data: object[], count: number }}
 */
export function unwrapGridEnvelope(response) {
  if (Array.isArray(response)) return { data: response, count: response.length };

  if (_.has(response, 'results')) {
    const data = response.results;
    return { data, count: response.count ?? data.length };
  }
  if (_.has(response, ['payload', 'results'])) {
    const data = response.payload.results;
    return {
      data,
      count: response.payload.count ?? _.get(response, ['payload', 'meta', 'total_count'], data.length),
    };
  }
  if (_.has(response, 'data')) {
    const data = response.data;
    return {
      data,
      count:
        response.count ?? _.get(response, ['meta', 'total'], _.get(response, ['meta', 'total_count'], data.length)),
    };
  }
  if (_.has(response, ['payload', 'data'])) {
    const data = response.payload.data;
    return {
      data,
      count:
        response.payload.count ??
        _.get(response, ['payload', 'meta', 'total'], _.get(response, ['payload', 'meta', 'total_count'], data.length)),
    };
  }

  return { data: [], count: 0 };
}

/**
 * Page a grid list fetcher to exhaustion and return every row.
 *
 * The caller binds filter/sort state and any wire serialization
 * (`toJsonApiParams`) or path segments (e.g. a site id) into `fetchPage`;
 * this loop only owns the offset/limit walk and the stop condition.
 *
 * The walk advances by the number of rows a page actually returned and stops
 * on the total the response reports, never on the size it asked for. A grid
 * endpoint caps what it will serve (`OffsetLimitPagination.max_limit`), so a
 * request for more comes back clamped — reading that shorter page as "the last
 * one" ends the walk after a single page and exports a fraction of the set.
 *
 * A response carrying no total is a whole result set by definition (a bare
 * array, an unpaginated payload), so it ends the walk after one page.
 *
 * @param {(page: { limit: number, offset: number }) => Promise<object|Array>} fetchPage
 * @param {{ pageSize?: number }} [options] - rows to request per page. Pass the
 *   endpoint's own maximum (the grid descriptor's `max_page_size`) so the
 *   request asks for what the server will actually serve.
 * @returns {Promise<object[]>} every row across all pages, envelope-unwrapped.
 */
export async function fetchAllPages(fetchPage, { pageSize = DEFAULT_PAGE_SIZE } = {}) {
  const allRows = [];
  let offset = 0;
  let hasMore = true;
  while (hasMore) {
    const response = await fetchPage({ limit: pageSize, offset });
    const { data, count } = unwrapGridEnvelope(response);
    if (data.length === 0) break;
    allRows.push(...data);
    offset += data.length;
    hasMore = allRows.length < count;
  }
  return allRows;
}
